import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, Users, TrendingUp, Monitor, Smartphone, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const SECTION_LABELS = {
  '/': 'Accueil',
  '/#accueil': 'Accueil',
  '/#race': 'Le Sibérien',
  '/#elevage': 'Notre Élevage',
  '/#chats': 'Nos Chats',
  '/#alumni': 'Adoptés',
  '/#portees': 'Portées',
  '/#blog': 'Blog',
  '/#temoignages': 'Témoignages',
  '/#adoption': 'Adoption',
  '/#contact': 'Contact',
};

function MiniBar({ value, max, color = 'bg-autumn' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full bg-sand/30 rounded-full h-2">
      <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-forest' }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-soft">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color === 'text-forest' ? 'bg-forest/10' : 'bg-autumn/10'}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <p className="font-heading text-2xl font-bold text-forest">{value}</p>
          <p className="font-body text-xs text-forest/50">{label}</p>
        </div>
      </div>
      {sub && <p className="font-body text-xs text-forest/40 mt-2">{sub}</p>}
    </div>
  );
}

export default function AnalyticsWidget() {
  const { getAuthHeader } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/analytics?days=${period}`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
    setLoading(false);
  };

  if (loading || !analytics) {
    return (
      <div className="bg-white/50 rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-sand/30 rounded w-40 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-20 bg-sand/20 rounded-xl" />
          <div className="h-20 bg-sand/20 rounded-xl" />
          <div className="h-20 bg-sand/20 rounded-xl" />
        </div>
      </div>
    );
  }

  const { totals, today, daily, top_pages, referrers, devices, hours } = analytics;
  const maxPageViews = Math.max(...(top_pages.map(p => p.views) || [1]));
  const desktopCount = devices.find(d => d.device === 'desktop')?.count || 0;
  const mobileCount = devices.find(d => d.device === 'mobile')?.count || 0;
  const totalDevices = desktopCount + mobileCount || 1;

  // Sparkline data for daily chart
  const maxDaily = Math.max(...daily.map(d => d.views), 1);
  const chartHeight = 60;

  return (
    <div className="space-y-4" data-testid="analytics-widget">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-autumn" />
          Statistiques de visite
        </h3>
        <div className="flex gap-1 bg-sand/30 rounded-lg p-0.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-3 py-1 rounded-md text-xs font-body font-medium transition-colors ${
                period === d ? 'bg-white text-forest shadow-sm' : 'text-forest/50 hover:text-forest'
              }`}
              data-testid={`analytics-period-${d}`}
            >
              {d}j
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Eye} label="Vues aujourd'hui" value={today.views} />
        <StatCard icon={Users} label="Visiteurs aujourd'hui" value={today.visitors} />
        <StatCard icon={Eye} label={`Vues (${period}j)`} value={totals.total_views} color="text-autumn" />
        <StatCard icon={Users} label={`Visiteurs (${period}j)`} value={totals.total_visitors} color="text-autumn" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Daily chart */}
        <div className="lg:col-span-2 bg-white rounded-xl p-4 shadow-soft">
          <p className="font-body text-sm font-semibold text-forest mb-3">Visites par jour</p>
          {daily.length === 0 ? (
            <p className="font-body text-sm text-forest/40 py-8 text-center">Pas encore de données</p>
          ) : (
            <div className="flex items-end gap-[2px]" style={{ height: chartHeight + 20 }}>
              {daily.slice(-30).map((day, idx) => {
                const h = Math.max(2, (day.views / maxDaily) * chartHeight);
                const label = day.date.slice(5);
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center group relative">
                    <div
                      className="w-full bg-forest/70 hover:bg-autumn rounded-t transition-colors cursor-default"
                      style={{ height: `${h}px` }}
                      title={`${day.date}: ${day.views} vues, ${day.visitors} visiteurs`}
                    />
                    {idx % 5 === 0 && (
                      <span className="text-[8px] text-forest/30 mt-1 font-body">{label}</span>
                    )}
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-1 bg-forest text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 font-body">
                      {day.views} vues - {day.visitors} visiteurs
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="bg-white rounded-xl p-4 shadow-soft">
          <p className="font-body text-sm font-semibold text-forest mb-3">Appareils</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Monitor className="w-4 h-4 text-forest/60" />
              <div className="flex-1">
                <div className="flex justify-between text-xs font-body mb-1">
                  <span className="text-forest">Ordinateur</span>
                  <span className="text-forest/50">{Math.round((desktopCount / totalDevices) * 100)}%</span>
                </div>
                <MiniBar value={desktopCount} max={totalDevices} color="bg-forest" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Smartphone className="w-4 h-4 text-autumn/60" />
              <div className="flex-1">
                <div className="flex justify-between text-xs font-body mb-1">
                  <span className="text-forest">Mobile</span>
                  <span className="text-forest/50">{Math.round((mobileCount / totalDevices) * 100)}%</span>
                </div>
                <MiniBar value={mobileCount} max={totalDevices} color="bg-autumn" />
              </div>
            </div>
          </div>

          {/* Peak hours */}
          {hours.length > 0 && (
            <div className="mt-4 pt-3 border-t border-sand/30">
              <p className="font-body text-xs font-semibold text-forest mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Heures de pointe
              </p>
              <div className="flex items-end gap-[1px] h-8">
                {Array.from({ length: 24 }, (_, h) => {
                  const hourData = hours.find(hd => hd.hour === h);
                  const count = hourData?.count || 0;
                  const maxH = Math.max(...hours.map(hd => hd.count), 1);
                  const barH = Math.max(1, (count / maxH) * 28);
                  return (
                    <div
                      key={h}
                      className="flex-1 bg-forest/20 hover:bg-autumn/60 rounded-t transition-colors"
                      style={{ height: `${barH}px` }}
                      title={`${h}h: ${count} vues`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between text-[8px] text-forest/30 font-body mt-0.5">
                <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top pages & Referrers */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <div className="bg-white rounded-xl p-4 shadow-soft">
          <p className="font-body text-sm font-semibold text-forest mb-3">Pages les plus vues</p>
          {top_pages.length === 0 ? (
            <p className="font-body text-sm text-forest/40 text-center py-4">Pas encore de données</p>
          ) : (
            <div className="space-y-2">
              {top_pages.map((page, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="font-body text-xs text-forest/30 w-4">{idx + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-forest truncate">
                      {SECTION_LABELS[page.page] || page.page}
                    </p>
                    <MiniBar value={page.views} max={maxPageViews} />
                  </div>
                  <span className="font-body text-xs text-forest/50 font-medium">{page.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referrers */}
        <div className="bg-white rounded-xl p-4 shadow-soft">
          <p className="font-body text-sm font-semibold text-forest mb-3">Sources de trafic</p>
          {referrers.length === 0 ? (
            <p className="font-body text-sm text-forest/40 text-center py-4">
              Pas encore de sources externes
            </p>
          ) : (
            <div className="space-y-2">
              {referrers.map((ref, idx) => {
                let source;
                try { source = new URL(ref.source).hostname; } catch { source = ref.source; }
                return (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="font-body text-sm text-forest truncate">{source}</span>
                    <span className="font-body text-xs text-forest/50 font-medium">{ref.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
